import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ClienteCuponesDisponibles from './ClienteCuponesDisponibles.jsx';

import * as globals from '../../components/GlobalScripts.js';
import '../../styles/cliente/menu_pedidos.css';

import ClientePedidoModal from './ClientePedidoModal.jsx';
import {
    DAY_LABELS,
    DAY_ORDER,
    ORDER_CUTOFF_HOUR_EC,
    computeDayDateMapFromWeekRange,
    dateOnly,
    fetchOrdersWeek,
    fetchPlatillosMap,
    fetchPublishedMenu,
    formatEcDate,
    getMinOrderDayEc,
    getTodayEc,
    cancelOrder,
} from './clienteMenuApi.js';

function PlusIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
    );
}
function PencilIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 20h9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path
                d="M16.5 3.5l4 4L8 20H4v-4L16.5 3.5z"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function TrashIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 7h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M6 7l1 14h10l1-14" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
            <path d="M9 7V4h6v3" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
        </svg>
    );
}
function EyeIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinejoin="round"
            />
            <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="2.2" />
        </svg>
    );
}

function pickLatestForSlot(orders, dayIso, type) {
    const list = (orders || []).filter((o) => dateOnly(o.order_date) === dayIso && o.type === type);

    const active = list.filter((o) => o.status !== 'cancelado');
    active.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
    if (active[0]) return active[0];

    list.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
    return list[0] || null;
}

function canUpdateOrder(order, minOrderDayIso) {
    if (!order) return false;
    const d = dateOnly(order.order_date);
    return d && d >= minOrderDayIso;
}

function canCancelOrder(order, hoy) {
    if (!order) return false;
    const d = dateOnly(order.order_date);
    return d && d >= hoy;
}

function SlotCard({
    label,
    type,
    dayIso,
    minOrderDayIso,
    menuOptionsCount,
    hoy,
    envios,
    expDay,
    order,
    onOrder,
    onUpdate,
    onCancel,
    onView,
}) {
    const isDayValid = !!dayIso;
    const isTodayOrPast = !isDayValid || dayIso <= hoy;
    const isClosedByCutoff = isDayValid && dayIso > hoy && dayIso < minOrderDayIso;
    const isExpired = !!expDay && expDay < hoy;

    const hasOrderActive = !!order && order.status !== 'cancelado';
    const hasOrderCanceled = !!order && order.status === 'cancelado';
    const orderDay = dateOnly(order?.order_date);

    const canUpdate = hasOrderActive && canUpdateOrder(order, minOrderDayIso);
    const canCancel = hasOrderActive && canCancelOrder(order, hoy);
    const isUpdateClosedByCutoff = hasOrderActive && !!orderDay && orderDay > hoy && orderDay < minOrderDayIso;

    const canOrder =
        isDayValid &&
        !hasOrderActive &&
        menuOptionsCount > 0 &&
        envios > 0 &&
        !isExpired &&
        dayIso >= minOrderDayIso;

    return (
        <div className="tw-orderweek-slot">
            <div className="tw-orderweek-slot__top">
                <div className="tw-orderweek-slot__label">{label}</div>

                <div className="tw-orderweek-slot__meta">
                    <span className="tw-orderweek-chip">{menuOptionsCount} opciones</span>

                    {hasOrderActive ? (
                        <span className="tw-orderweek-chip tw-orderweek-chip--ok">Programado</span>
                    ) : hasOrderCanceled ? (
                        <span className="tw-orderweek-chip tw-orderweek-chip--muted">Cancelado</span>
                    ) : (
                        <span className="tw-orderweek-chip tw-orderweek-chip--muted">Sin pedido</span>
                    )}
                </div>
            </div>

            {hasOrderActive ? (
                <div className="tw-orderweek-slot__actions">
                    <button className="tw-orderweek-btn tw-orderweek-btn--ghost" onClick={onView} type="button">
                        <EyeIcon /> ver orden
                    </button>

                    <>
                        {canUpdate ? (
                            <button className="tw-orderweek-btn tw-orderweek-btn--primary" onClick={onUpdate} type="button">
                                <PencilIcon /> 
                            </button>
                        ) : null}

                        {canCancel ? (
                            <button className="tw-orderweek-btn tw-orderweek-btn--danger" onClick={onCancel} type="button">
                                <TrashIcon /> 
                            </button>
                        ) : null}

                        {isUpdateClosedByCutoff ? (
                            <div className="tw-orderweek-muted">{`Los cambios para este día cerraron a las ${ORDER_CUTOFF_HOUR_EC}:00 hora Ecuador`}</div>
                        ) : null}

                        {!canUpdate && !canCancel ? (
                            <div className="tw-orderweek-muted">{isTodayOrPast ? 'Pasado: solo ver' : 'No editable'}</div>
                        ) : null}
                    </>
                </div>
            ) : (
                <div className="tw-orderweek-slot__actions">
                    {hasOrderCanceled ? (
                        <button className="tw-orderweek-btn tw-orderweek-btn--ghost" onClick={onView} type="button">
                            <EyeIcon /> Ver
                        </button>
                    ) : null}

                    <button
                        className={`tw-orderweek-btn ${canOrder ? 'tw-orderweek-btn--primary' : 'tw-orderweek-btn--disabled'}`}
                        onClick={canOrder ? onOrder : undefined}
                        type="button"
                        disabled={!canOrder}
                        title={
                            !isDayValid
                                ? 'Fecha inválida'
                                : isExpired
                                    ? 'Tu plan está expirado'
                                    : envios <= 0
                                        ? 'No tienes envíos'
                                        : isTodayOrPast
                                            ? 'No puedes pedir para hoy'
                                            : isClosedByCutoff
                                                ? `Los pedidos para este día cierran a las ${ORDER_CUTOFF_HOUR_EC}:00 hora Ecuador`
                                            : menuOptionsCount <= 0
                                                ? 'No hay opciones'
                                                : ''
                        }
                    >
                        <PlusIcon /> Ordenar
                    </button>

                    {isExpired ? (
                        <div className="tw-orderweek-muted">Plan expirado: no puedes ordenar</div>
                    ) : envios <= 0 ? (
                        <div className="tw-orderweek-muted">Sin envíos disponibles</div>
                    ) : isTodayOrPast ? (
                        <div className="tw-orderweek-muted">No puedes pedir para hoy</div>
                    ) : isClosedByCutoff ? (
                        <div className="tw-orderweek-muted">{`El pedido para este día cerró a las ${ORDER_CUTOFF_HOUR_EC}:00 hora Ecuador`}</div>
                    ) : menuOptionsCount <= 0 ? (
                        <div className="tw-orderweek-muted">No hay opciones</div>
                    ) : null}
                </div>
            )}
        </div>
    );
}

export default function Cliente_menu_pedidos() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [loadErr, setLoadErr] = useState('');

    const [menu, setMenu] = useState(null);
    const [dishMap, setDishMap] = useState({});
    const [orders, setOrders] = useState([]);

    const [account, setAccount] = useState(() => globals.get_user() || {});
    const envios = Number(account?.envios || 0) || 0;
    const nivel = Number(account?.nivel || 1) || 1;
    const expDay = String(account?.envios_expira_at || '').slice(0, 10);

    const hoy = getTodayEc();
    const minOrderDayIso = getMinOrderDayEc();

    const busyRef = useRef(0);
    const busyWrap = async (fn) => {
        try {
            if (busyRef.current === 0) {
                globals.openViewCharge();
                await new Promise(requestAnimationFrame);
            }
            busyRef.current += 1;
            return await fn();
        } finally {
            busyRef.current = Math.max(0, busyRef.current - 1);
            if (busyRef.current === 0) globals.closeViewCharge();
        }
    };

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('view');
    const [modalOrder, setModalOrder] = useState(null);
    const [cancelBusy, setCancelBusy] = useState(false);

    const activeDays = Array.isArray(menu?.week?.active_days) ? menu.week.active_days : [];

    const dayDateMap = useMemo(() => {
        const s = menu?.week?.start_date;
        const e = menu?.week?.end_date;
        return computeDayDateMapFromWeekRange(s, e);
    }, [menu]);

    const dayKeysToRender = useMemo(() => {
        const set = new Set(activeDays);
        return DAY_ORDER.filter((k) => set.has(k));
    }, [activeDays]);

    const optionsCountByDayType = useMemo(() => {
        const out = {};
        dayKeysToRender.forEach((dayKey) => {
            const opts = Array.isArray(menu?.days?.[dayKey]?.options) ? menu.days[dayKey].options : [];
            const lunch = opts.filter((o) => o.scope === 'lunch' || o.scope === 'both').length;
            const dinner = opts.filter((o) => o.scope === 'dinner' || o.scope === 'both').length;
            out[`${dayKey}|almuerzo`] = lunch;
            out[`${dayKey}|cena`] = dinner;
        });
        return out;
    }, [menu, dayKeysToRender]);

    const canShowDinner = useMemo(() => {
        if (nivel === 3) return true;
        const hasAnyDinner = (orders || []).some((o) => o.type === 'cena');
        return hasAnyDinner;
    }, [nivel, orders]);

    const loadAll = async () => {
        setLoading(true);
        setLoadErr('');

        try {
            await busyWrap(async () => {
                const published = await fetchPublishedMenu();
                if (!published) {
                    setMenu(null);
                    setLoadErr('No hay menú publicado.');
                    setLoading(false);
                    return;
                }
                setMenu(published);

                const map = await fetchPlatillosMap();
                setDishMap(map);

                const weekStart = published?.week?.start_date;
                const weekEnd = published?.week?.end_date;

                const { orders: ords, account: acc } = await fetchOrdersWeek({
                    start_date: weekStart,
                    end_date: weekEnd,
                });

                setOrders(Array.isArray(ords) ? ords : []);

                if (acc) {
                    setAccount(acc);
                    const current = globals.get_user() || {};
                    localStorage.setItem('user', JSON.stringify({ ...current, ...acc }));
                } else {
                    await globals.validateSession?.();
                    setAccount(globals.get_user() || {});
                }

                setLoading(false);
            });
        } catch (e) {
            setLoading(false);
            setLoadErr('Error cargando menú/pedidos.');
            globals.createAlert?.({
                type: 'error',
                msg_title: 'Error',
                msg: String(e?.message || 'No se pudo cargar'),
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 4000,
            });
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const openView = (order) => {
        setModalMode('view');
        setModalOrder(order);
        setModalOpen(true);
    };

    const openCancel = (order) => {
        setModalMode('cancel');
        setModalOrder(order);
        setModalOpen(true);
    };

    const handleCancelConfirm = async () => {
        if (!modalOrder?.id) return;
        setCancelBusy(true);

        try {
            const res = await cancelOrder({ id: modalOrder.id });

            if (res?.mensaje !== 'success') {
                globals.createAlert?.({
                    type: 'error',
                    msg_title: 'Error',
                    msg: res?.mensaje || 'No se pudo cancelar',
                    effect: 'opacidad 2s, desplazamiento 2s',
                    time_life: 4000,
                });
                setCancelBusy(false);
                return;
            }

            await loadAll();

            globals.createAlert?.({
                type: 'success',
                msg_title: 'Listo',
                msg: res?.refund ? 'Pedido cancelado. Se devolvió 1 envío.' : 'Pedido cancelado.',
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 3000,
            });

            setCancelBusy(false);
            setModalOpen(false);
            setModalOrder(null);
        } catch (e) {
            setCancelBusy(false);
            globals.createAlert?.({
                type: 'error',
                msg_title: 'Error',
                msg: String(e?.message || 'No se pudo cancelar'),
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 4000,
            });
        }
    };

    const goWizard = ({ dayKey, type, mode, existingOrder }) => {
        navigate(`/cliente/menu/${dayKey}/${type}`, {
            state: {
                dayKey,
                type,
                dayIso: dayDateMap?.[dayKey] || '',
                mode,
                existingOrder: existingOrder || null,
                menu: menu || null,
                dishMap: dishMap || null,
                account: {
                    envios: Number(account?.envios || 0) || 0,
                    nivel: Number(account?.nivel || 1) || 1,
                    envios_expira_at: account?.envios_expira_at || '',
                },
            },
        });
    };

    if (loading) {
        return (
            <div className="tw-orderweek-page">
                <div className="tw-orderweek-skel">Cargando menú...</div>
            </div>
        );
    }

    if (loadErr) {
        return (
            <div className="tw-orderweek-page">
                <div className="tw-orderweek-alert tw-orderweek-alert--error">{loadErr}</div>
                <button className="tw-orderweek-btn tw-orderweek-btn--primary" onClick={loadAll} type="button">
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="tw-orderweek-page container-section flex-column gap-20">
            <div className="tw-orderweek-top">
                <div className="tw-orderweek-title">
                    <div className="tw-orderweek-h1">Planifica tu semana</div>
                    <div className="tw-orderweek-sub">Selecciona tus pedidos por día.</div>
                </div>

                <div className="tw-orderweek-banner">
                    <div className="tw-orderweek-banner__left">
                        <div className="tw-orderweek-banner__big">{envios}</div>
                        <div className="tw-orderweek-banner__txt">
                            <div className="tw-orderweek-banner__label">Envíos disponibles</div>
                            <div className="tw-orderweek-banner__muted">Nivel {nivel}</div>
                        </div>
                    </div>

                    <div className="tw-orderweek-banner__right">
                        <div className="tw-orderweek-banner__label">Expiran</div>
                        <div className="tw-orderweek-banner__date">{expDay || '—'}</div>
                        <div className="tw-orderweek-banner__muted">Hoy: {hoy}</div>
                    </div>
                </div>
            </div>

            <ClienteCuponesDisponibles renewTo="/cliente/renovacion" />

            <div className="tw-orderweek-days">
                {dayKeysToRender.map((dayKey) => {
                    const dayIso = dayDateMap?.[dayKey] || '';
                    const dayName = DAY_LABELS?.[dayKey] || dayKey;

                    const lunchOrder = pickLatestForSlot(orders, dayIso, 'almuerzo');
                    const dinnerOrder = pickLatestForSlot(orders, dayIso, 'cena');

                    const lunchCount = optionsCountByDayType[`${dayKey}|almuerzo`] || 0;
                    const dinnerCount = optionsCountByDayType[`${dayKey}|cena`] || 0;

                    const isEditable = !!dayIso && dayIso > hoy;

                    return (
                        <div className="tw-orderweek-daycard" key={dayKey}>
                            <div className="tw-orderweek-daycard__head">
                                <div className="tw-orderweek-daycard__left">
                                    <div className="tw-orderweek-daycard__day">{dayName}</div>
                                    <div className="tw-orderweek-daycard__date">
                                        {dayIso ? dayIso : '—'}
                                    </div>
                                </div>

                                <div className="tw-orderweek-daycard__right">
                                    {isEditable ? (
                                        <span className="tw-orderweek-chip">Editable</span>
                                    ) : (
                                        <span className="tw-orderweek-chip tw-orderweek-chip--muted">No editable</span>
                                    )}
                                </div>
                            </div>

                            <div className="tw-orderweek-daycard__body">
                                <SlotCard
                                    label="Almuerzo"
                                    type="almuerzo"
                                    dayIso={dayIso}
                                    minOrderDayIso={minOrderDayIso}
                                    menuOptionsCount={lunchCount}
                                    hoy={hoy}
                                    envios={envios}
                                    expDay={expDay}
                                    order={lunchOrder}
                                    onOrder={() => goWizard({ dayKey, type: 'almuerzo', mode: 'create', existingOrder: null })}
                                    onUpdate={() => goWizard({ dayKey, type: 'almuerzo', mode: 'update', existingOrder: lunchOrder })}
                                    onCancel={() => openCancel(lunchOrder)}
                                    onView={() => openView(lunchOrder)}
                                />

                                {canShowDinner ? (
                                    <SlotCard
                                        label="Cena"
                                        type="cena"
                                        dayIso={dayIso}
                                        minOrderDayIso={minOrderDayIso}
                                        menuOptionsCount={dinnerCount}
                                        hoy={hoy}
                                        envios={envios}
                                        expDay={expDay}
                                        order={dinnerOrder}
                                        onOrder={() => goWizard({ dayKey, type: 'cena', mode: 'create', existingOrder: null })}
                                        onUpdate={() => goWizard({ dayKey, type: 'cena', mode: 'update', existingOrder: dinnerOrder })}
                                        onCancel={() => openCancel(dinnerOrder)}
                                        onView={() => openView(dinnerOrder)}
                                    />
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>

            <ClientePedidoModal
                open={modalOpen}
                mode={modalMode}
                title={modalMode === 'cancel' ? 'Cancelar pedido' : 'Detalle del pedido'}
                order={modalOrder}
                dishMap={dishMap}
                enviosExpiraAt={account?.envios_expira_at || ''}
                onClose={() => {
                    setModalOpen(false);
                    setModalOrder(null);
                }}
                onConfirmCancel={handleCancelConfirm}
                isLoading={cancelBusy}
            />
        </div>
    );
}
